import React, { useEffect, useRef, useState } from "react";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import styles from "./styles.module.css";
import Pagination from "../../Pagination";

const sxIconeAzulMarinho = {
  color: "#18316e",
  "&:hover": { backgroundColor: "rgba(24, 49, 110, 0.1)" },
};

const sxIconePerigo = {
  color: "#b71c1c",
  "&:hover": { backgroundColor: "rgba(183, 28, 28, 0.08)" },
};

const TableViewv2 = ({ columns, data, hasBotoes, colunaHover, paginationConfig }) => {
  const isServerSide = paginationConfig != null && typeof paginationConfig.onServerPageChange === "function";
  const [filteredData, setFilteredData] = useState(data);
  const [visibleItems, setVisibleItems] = useState([]);
  const [loadedChunks, setLoadedChunks] = useState(0);
  const observerRef = useRef(null);
  const loadingRef = useRef(false);
  const CHUNK_SIZE = 20;
  const source = isServerSide ? filteredData : visibleItems;
  const rowsToRender = Array.isArray(source) ? source : [];

  const colunaAcoesIconesEnxuta =
    hasBotoes &&
    rowsToRender.length > 0 &&
    rowsToRender.every(
      (row) => Array.isArray(row.btn) && row.btn.length > 1
    );

  const acoesThClassName = `${styles.colunaAcoes} ${
    colunaAcoesIconesEnxuta ? styles.colunaAcoesIcones : styles.colunaAcoesUnico
  }`;

  const handleStyleNumerico = (item, columnKey) => {
    // Colunas que devem ficar centralizadas
    const centerAlignedColumns = ['tipoDisplay', 'uf', 'statusDisplay'];

    if (centerAlignedColumns.includes(columnKey)) {
      return { textAlign: "center", whiteSpace: "nowrap" };
    }

    // Colunas monetárias que devem ficar alinhadas à direita
    const rightAlignedColumns = [
      'valorRubrica', 
      'totalGeral', 
      'totalGeralCorrigido', 
      'totalGeralCalculado',
      'totalCprb', 
      'totalCprbCorrigido', 
      'totalCprbCalculado',
      'cargaSefipCprb'
    ];

    if (rightAlignedColumns.includes(columnKey)) {
      return { textAlign: "right", whiteSpace: "nowrap" };
    }

    // Coluna de incidência com truncamento para evitar sobreposição
    if (columnKey === 'incidenciaRubrica') {
      return { textAlign: "left", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "250px" };
    }

    // Demais colunas alinhadas à esquerda
    return { textAlign: "left", whiteSpace: "nowrap" };
  };

  useEffect(() => {
    if (isServerSide) setFilteredData(data || []);
  }, [data, isServerSide]);

  useEffect(() => {
    if (isServerSide) return;
    setLoadedChunks(0);
    setVisibleItems([]);
    loadingRef.current = false;
  }, [filteredData, isServerSide]);

  // Efeito para carregar os chunks visíveis (apenas client-side)
  const safeFilteredLength = Array.isArray(filteredData) ? filteredData.length : 0;
  useEffect(() => {
    if (isServerSide || safeFilteredLength === 0 || loadingRef.current) return;
    loadingRef.current = true;
    const endIndex = Math.min((loadedChunks + 1) * CHUNK_SIZE, safeFilteredLength);
    const newItems = (Array.isArray(filteredData) ? filteredData : []).slice(0, endIndex);
    setVisibleItems(newItems);
    loadingRef.current = false;
  }, [filteredData, loadedChunks, isServerSide]);

  // Efeito para observar a última linha e carregar mais (apenas client-side)
  useEffect(() => {
    if (isServerSide || !observerRef.current || visibleItems.length >= safeFilteredLength) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) setLoadedChunks(prev => prev + 1);
      },
      { threshold: 0.1 }
    );
    observer.observe(observerRef.current);
    return () => observer.disconnect();
  }, [visibleItems.length, safeFilteredLength, isServerSide]);

  return (
    <>
      <table className={styles.table}>
        <thead>
          <tr>
            {columns.map((col, i) => {
              // Define alinhamento do cabeçalho baseado na coluna
              const centerAlignedColumns = ['tipoDisplay', 'uf', 'statusDisplay'];
              const rightAlignedColumns = [
                'valorRubrica', 
                'totalGeral', 
                'totalGeralCorrigido', 
                'totalGeralCalculado',
                'totalCprb', 
                'totalCprbCorrigido', 
                'totalCprbCalculado',
                'cargaSefipCprb'
              ];
              
              // Primeiro verifica se há alinhamento explícito na definição da coluna
              let headerStyle = {};
              if (col.align) {
                headerStyle = { textAlign: col.align };
              } else {
                // Fallback para alinhamento baseado no tipo de coluna
                headerStyle = { textAlign: 'left' };
                if (centerAlignedColumns.includes(col.key)) {
                  headerStyle = { textAlign: 'center' };
                } else if (rightAlignedColumns.includes(col.key)) {
                  headerStyle = { textAlign: 'right' };
                }
              }

              // Estilo especial para coluna de incidência
              if (col.key === 'incidenciaRubrica') {
                headerStyle = { ...headerStyle, maxWidth: '250px' };
              }

              // Adiciona largura fixa se especificada na coluna
              if (col.width) {
                headerStyle = { ...headerStyle, width: col.width, minWidth: col.width, maxWidth: col.width };
              }

              // Aplica estilo de destaque apenas quando há hover (preview visual)
              const isColunaHover = colunaHover === col.key;
              const finalHeaderStyle = colunaHover 
                ? {
                    ...headerStyle,
                    backgroundColor: isColunaHover ? "#e8f4fd" : headerStyle.backgroundColor || "#F5F5F5",
                    transition: "background-color 0.2s ease",
                    borderLeft: isColunaHover ? "2px solid #1976d2" : "none",
                    borderRight: isColunaHover ? "2px solid #1976d2" : "none",
                  }
                : headerStyle;

              return (
                <th 
                  key={i} 
                  className={styles.columns} 
                  style={finalHeaderStyle}
                  data-column={col.key}
                >
                  {col.label}
                </th>
              );
            })}
            {hasBotoes ? (
              <th className={acoesThClassName} style={{ textAlign: "center" }}>
                Ações
              </th>
            ) : (
              ""
            )}
          </tr>
        </thead>
        <tbody>
          {rowsToRender.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className={styles.tableBody}
                style={row?.style ?? {}}
                ref={!isServerSide && rowIndex === rowsToRender.length - 1 ? observerRef : null}
              >
                {columns.map((col, colIndex) => {
                  const cellValue = typeof row[col.key] === "function"
                    ? row[col.key]()
                    : row[col.key];
                  
                  const cellStyle = handleStyleNumerico(row[col.key], col.key);
                  
                  // Respeita o alinhamento especificado na coluna, se existir
                  const alignmentStyle = col.align 
                    ? { textAlign: col.align }
                    : {};
                  
                  // Adiciona largura fixa se especificada na coluna
                  const baseCellStyle = col.width 
                    ? { ...cellStyle, ...alignmentStyle, width: col.width, minWidth: col.width, maxWidth: col.width }
                    : { ...cellStyle, ...alignmentStyle };
                  
                  // Aplica estilo de destaque apenas quando há hover (preview visual leve)
                  const isColunaHover = colunaHover === col.key;
                  const finalCellStyle = colunaHover 
                    ? {
                        ...baseCellStyle,
                        backgroundColor: isColunaHover ? "#f0f7ff" : "transparent",
                        transition: "background-color 0.2s ease",
                      }
                    : baseCellStyle;
                  
                  const cellContent = col.key === 'incidenciaRubrica' && cellValue ? (
                    <Tooltip title={String(cellValue)} arrow placement="top">
                      <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {cellValue}
                      </span>
                    </Tooltip>
                  ) : (
                    cellValue
                  );

                  return (
                    <td 
                      key={colIndex} 
                      data-column={col.key}
                      style={finalCellStyle}
                    >
                      {cellContent}
                    </td>
                  );
                })}
                {hasBotoes ? (
                  <td
                    className={`${styles.colunaAcoes} ${
                      row.btn.length > 1
                        ? styles.colunaAcoesIcones
                        : styles.colunaAcoesUnico
                    }`}
                    style={{ textAlign: "center" }}
                  >
                    {row.btn.length === 1 ? (
                      <div className={styles.btnContainer}>
                        <button
                          className={styles.uniqueBtn}
                          onClick={row.btn[0].onClick}
                          disabled={row.btn[0].disabled}
                        >
                          <span style={{ marginRight: ".5rem" }}>
                            {row.btn[0].icon}
                          </span>
                          {row.btn[0].title}
                        </button>
                      </div>
                    ) : (
                      <div className={styles.acoesIcones}>
                        {row.btn.map((item, index) => (
                          <Tooltip key={index} title={item.title} arrow>
                            <span>
                              <IconButton
                                onClick={item.onClick}
                                disabled={item.disabled}
                                size="small"
                                aria-label={item.title}
                                sx={
                                  item.variant === "danger"
                                    ? sxIconePerigo
                                    : sxIconeAzulMarinho
                                }
                              >
                                {item.icon}
                              </IconButton>
                            </span>
                          </Tooltip>
                        ))}
                      </div>
                    )}
                  </td>
                ) : (
                  ""
                )}
              </tr>
          ))}
        </tbody>
      </table>
      <Pagination
        data={data}
        filteredData={filteredData}
        setFilteredData={setFilteredData}
        {...(paginationConfig || {})}
      />
    </>
  );
};

export default TableViewv2;
